import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Mail, Lock, Eye, EyeOff, User, MailCheck } from 'lucide-react';
import { Button, Input, TurnstileCaptcha, ErrorDisplay, Modal, PasswordRequirements, isPasswordValid } from '../components/common';
import type { TurnstileCaptchaHandle } from '../components/common';
import { useAuthStore } from '../stores/authStore';
import { showToast } from '../components/common/Toast';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

interface LoginFormData {
  email: string;
  password: string;
  captchaToken?: string;
}

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  captchaToken?: string;
}

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register: registerUser, isLoading, error, validationErrors, clearError, registrationSuccessEmail, clearRegistrationSuccess, isAuthenticated } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const isRegisterMode = location.pathname === '/register';

  const loginCaptchaTokenRef = useRef<string>('');
  const registerCaptchaTokenRef = useRef<string>('');
  const loginCaptchaWidgetRef = useRef<TurnstileCaptchaHandle>(null);
  const registerCaptchaWidgetRef = useRef<TurnstileCaptchaHandle>(null);
  const [captchaError, setCaptchaError] = useState('');

  const {
    register: registerLogin,
    handleSubmit: handleLoginSubmit,
    formState: { errors: loginErrors },
  } = useForm<LoginFormData>();

  const {
    register: registerForm,
    handleSubmit: handleRegisterSubmit,
    formState: { errors: registerErrors },
    watch,
  } = useForm<RegisterFormData>();

  const watchPassword = watch('password', '');
  const watchConfirmPassword = watch('confirmPassword', '');

  const onLoginSubmit = async (data: LoginFormData) => {
    if (TURNSTILE_SITE_KEY && !loginCaptchaTokenRef.current) {
      setCaptchaError(t('auth.captcha.pleaseComplete'));
      return;
    }
    setCaptchaError('');

    await login({
      email: data.email,
      password: data.password,
      captchaToken: TURNSTILE_SITE_KEY ? loginCaptchaTokenRef.current : undefined,
    });

    loginCaptchaTokenRef.current = '';
    loginCaptchaWidgetRef.current?.reset();
  };

  const onRegisterSubmit = async (data: RegisterFormData) => {
    if (!isPasswordValid(data.password)) {
      showToast(t('auth.validation.passwordRequirements'), 'error');
      return;
    }

    if (data.password !== data.confirmPassword) {
      showToast(t('auth.validation.passwordsDoNotMatch'), 'error');
      return;
    }

    if (TURNSTILE_SITE_KEY && !registerCaptchaTokenRef.current) {
      setCaptchaError(t('auth.captcha.pleaseComplete'));
      return;
    }
    setCaptchaError('');

    await registerUser({
      email: data.email,
      password: data.password,
      firstName: data.firstName,
      lastName: data.lastName,
      captchaToken: TURNSTILE_SITE_KEY ? registerCaptchaTokenRef.current : undefined,
    });

    registerCaptchaTokenRef.current = '';
    registerCaptchaWidgetRef.current?.reset();
  };

  const handleLoginCaptchaSuccess = useCallback((token: string) => {
    loginCaptchaTokenRef.current = token;
    setCaptchaError('');
  }, []);

  const handleRegisterCaptchaSuccess = useCallback((token: string) => {
    registerCaptchaTokenRef.current = token;
    setCaptchaError('');
  }, []);

  const handleCaptchaError = useCallback(() => {
    setCaptchaError(t('auth.captcha.verificationFailed'));
  }, []);

  const handleLoginCaptchaExpired = useCallback(() => {
    loginCaptchaTokenRef.current = '';
    setCaptchaError(t('auth.captcha.expired'));
  }, []);

  const handleRegisterCaptchaExpired = useCallback(() => {
    registerCaptchaTokenRef.current = '';
    setCaptchaError(t('auth.captcha.expired'));
  }, []);

  // Show server error as toast for register mode
  React.useEffect(() => {
    if (isRegisterMode && error) {
      showToast(error, 'error');
      clearError();
    }
  }, [error, isRegisterMode, clearError]);

  const handleModalClose = useCallback(() => {
    clearRegistrationSuccess();
    navigate('/login');
  }, [clearRegistrationSuccess, navigate]);

  if (isAuthenticated) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-600">TaskFly</h1>
          <p className="text-gray-500 mt-2">
            {isRegisterMode ? 'Create your account' : 'Sign in to your account'}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          {isRegisterMode ? (
            <form onSubmit={handleRegisterSubmit(onRegisterSubmit)} className="space-y-4">
              {/* Server errors shown as toast, not as banner */}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  placeholder={t('auth.placeholder.firstName')}
                  leftIcon={<User className="w-4 h-4" />}
                  error={registerErrors.firstName?.message}
                  {...registerForm('firstName', {
                    required: t('auth.validation.firstNameRequired'),
                  })}
                />
                <Input
                  label="Last Name"
                  placeholder={t('auth.placeholder.lastName')}
                  leftIcon={<User className="w-4 h-4" />}
                  error={registerErrors.lastName?.message}
                  {...registerForm('lastName', {
                    required: t('auth.validation.lastNameRequired'),
                  })}
                />
              </div>

              <Input
                label="Email"
                type="email"
                placeholder={t('auth.placeholder.email')}
                leftIcon={<Mail className="w-4 h-4" />}
                error={registerErrors.email?.message}
                {...registerForm('email', {
                  required: t('auth.validation.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('auth.validation.invalidEmail'),
                  },
                })}
              />

              <div className="relative">
                <Input
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••••••"
                  leftIcon={<Lock className="w-4 h-4" />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-pressed={showPassword}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="focus:outline-none min-h-[44px] min-w-[44px] flex items-center justify-center"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  }
                  error={registerErrors.password?.message}
                  {...registerForm('password', {
                    required: t('auth.validation.passwordRequired'),
                    minLength: {
                      value: 12,
                      message: t('auth.validation.passwordMinLength', { count: 12 }),
                    },
                  })}
                />
              </div>

              <Input
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••••••"
                leftIcon={<Lock className="w-4 h-4" />}
                error={registerErrors.confirmPassword?.message}
                {...registerForm('confirmPassword', {
                  required: t('auth.validation.confirmPasswordRequired'),
                  validate: (value, formValues) =>
                    value === formValues.password || t('auth.validation.passwordsDoNotMatch'),
                })}
              />

              {/* Real-time password requirements */}
              <PasswordRequirements
                password={watchPassword}
                confirmPassword={watchConfirmPassword}
              />

              {TURNSTILE_SITE_KEY && (
                <div className="mt-4">
                  <TurnstileCaptcha
                    ref={registerCaptchaWidgetRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={handleRegisterCaptchaSuccess}
                    onError={handleCaptchaError}
                    onExpired={handleRegisterCaptchaExpired}
                    theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                  />
                  {captchaError && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
                      {captchaError}
                    </p>
                  )}
                </div>
              )}

              <Button type="submit" className="w-full" isLoading={isLoading}>{t('common.signUp')}</Button>
            </form>
          ) : (
            <form onSubmit={handleLoginSubmit(onLoginSubmit)} className="space-y-4">
              <ErrorDisplay
                message={error || captchaError}
                validationErrors={validationErrors}
                dismissible
                onDismiss={clearError}
              />

              <Input
                label="Email"
                type="email"
                placeholder={t('auth.placeholder.email')}
                leftIcon={<Mail className="w-4 h-4" />}
                error={loginErrors.email?.message}
                {...registerLogin('email', {
                  required: t('auth.validation.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('auth.validation.invalidEmail'),
                  },
                })}
              />

              <div className="relative">
                <Input
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  leftIcon={<Lock className="w-4 h-4" />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-pressed={showPassword}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="focus:outline-none min-h-[44px] min-w-[44px] flex items-center justify-center"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  }
                  error={loginErrors.password?.message}
                  {...registerLogin('password', {
                    required: t('auth.validation.passwordRequired'),
                  })}
                />
              </div>

              {TURNSTILE_SITE_KEY && (
                <div className="mt-4">
                  <TurnstileCaptcha
                    ref={loginCaptchaWidgetRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={handleLoginCaptchaSuccess}
                    onError={handleCaptchaError}
                    onExpired={handleLoginCaptchaExpired}
                    theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                  />
                </div>
              )}

              <Button type="submit" className="w-full" isLoading={isLoading}>{t('common.signIn')}</Button>
            </form>
          )}

          <div className="mt-6 text-center text-sm text-gray-500">
            {isRegisterMode ? (
              <>
                Already have an account?{' '}
                <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                  Sign in
                </Link>
              </>
            ) : (
              <>
                Don't have an account?{' '}
                <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
                  Sign up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Registration success modal */}
      <Modal
        isOpen={!!registrationSuccessEmail}
        onClose={handleModalClose}
        onClear={handleModalClose}
        showCloseButton={true}
        size="sm"
      >
        <div className="flex flex-col items-center py-8 px-4 text-center">
          {/* Animated email icon */}
          <div className="relative mb-6">
            <div className="w-20 h-20 rounded-full bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center animate-bounce-once">
              <MailCheck className="w-10 h-10 text-primary-600 dark:text-primary-400" />
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('auth.success.registrationCheckInbox')}</h2>

          <p className="text-gray-500 dark:text-gray-400 mb-2">
            We&apos;ve sent a confirmation email to
          </p>
          <p className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-6">
            {registrationSuccessEmail}
          </p>

          <p className="text-sm text-gray-400 dark:text-gray-500">{t('auth.success.registrationEmailHint')}</p>
        </div>
      </Modal>

      {/* Inline style for the bounce animation */}
      <style>{`
        @keyframes bounceOnce {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.1); }
          70% { transform: scale(0.9); }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-bounce-once {
          animation: bounceOnce 0.6s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
